/** Binary steps, labelled the way firmware datasheets and toolchains label them. */
const UNITS = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'] as const
const STEP = 1024

/**
 * A byte count for display: binary steps, `KB`-style labels, at most two decimals.
 *
 * `KB` here means 1024, which is what an ESP32 datasheet or an `esptool` run means by it. The
 * strictly correct `KiB` is rare in hardware tooling, so it reads as unfamiliar to the people
 * uploading firmware here. Nothing in the app has ever used decimal (1000) steps.
 *
 * Trailing zeros are trimmed, so a 2 GiB limit reads "2 GB" rather than "2.00 GB", while a real
 * fraction still shows both places.
 */
export function formatBytes(bytes: number | null | undefined): string {
	if (bytes === null || bytes === undefined || !Number.isFinite(bytes) || bytes < 0) {
		return 'N/A'
	}
	if (bytes === 0) {
		return '0 B'
	}

	const index = Math.min(Math.floor(Math.log(bytes) / Math.log(STEP)), UNITS.length - 1)
	return `${Number((bytes / STEP ** index).toFixed(2))} ${UNITS[index]}`
}

/** Throughput, in the same units as `formatBytes`. */
export function formatBytesPerSecond(bytesPerSecond: number | null | undefined): string {
	const size = formatBytes(bytesPerSecond)
	return size === 'N/A' ? size : `${size}/s`
}
